import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {firstValueFrom} from "rxjs";
import {environment} from "../../environments/environment";
import {Anagrafica} from "../_models/Anagrafica";
import {AnagraficheFilter} from "../_models/AnagraficheFilter";
import {SearchResult} from "../_models/SearchResult";
import {Sede} from "../_models/Sede";
import {AnagraficaImportResult} from "../_models/AnagrafficaImportResult";
import {ModalitaSovrascrittura} from "../_models/ModalitaSovrascrittura";

@Injectable({ providedIn: 'root' })
export class RegistryService {
  constructor(private http: HttpClient) { }

  async getAnagraficheFilteredAsync(
    orgId: number,
    search: string,
    page: number,
    count: number,
    sortField: string,
    sortAsc: boolean,
    filter: AnagraficheFilter = {}
  ) {
    return firstValueFrom(this.http.post<SearchResult<Anagrafica>>(
      `${environment.localApiUrl}registry/Anagrafiche/AnagraficheFiltered?orgId=${orgId}`,
      { search, page, count, sortField, sortAsc, ...filter }
    ));
  }
  
  async getAnagraficaAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<Anagrafica>(`${environment.localApiUrl}registry/Anagrafiche/AnagraficaFromId?orgId=${orgId}&id=${id}`));
  }

  async createAnagraficaAsync(anagrafica: Anagrafica) {
    return firstValueFrom(this.http.post<Anagrafica>(`${environment.localApiUrl}registry/Anagrafiche/CreateAnagrafica`, anagrafica));
  }
  
  async updateAnagraficaAsync(anagrafica: Anagrafica) {
    return firstValueFrom(this.http.post<Anagrafica>(`${environment.localApiUrl}registry/Anagrafiche/UpdateAnagrafica`, anagrafica));
  }
  
  async deleteAnagraficaAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<number>(`${environment.localApiUrl}registry/Anagrafiche/DeleteAnagrafica?orgId=${orgId}&id=${id}`));
  }

  
  async getSediOfAnagraficaFilteredAsync(orgId: number, anagraficaId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Sede>>(`${environment.localApiUrl}registry/Sedi/SediOfAnagraficaFiltered?orgId=${orgId}`,
      { anagraficaId, search, page, count, sortField, sortAsc }));
  }

  async createSedeAsync(sede: Sede) {
    return firstValueFrom(this.http.post<Sede>(`${environment.localApiUrl}registry/Sedi/CreateSede`, sede));
  }

  async updateSedeAsync(sede: Sede) {
    return firstValueFrom(this.http.post<Sede>(`${environment.localApiUrl}registry/Sedi/UpdateSede`, sede));
  }

  async deleteASedeAsync(orgId: number, anagraficaId: number, id: number) {
    return firstValueFrom(this.http.get<number>(`${environment.localApiUrl}registry/Sedi/DeleteSede?orgId=${orgId}&anagraficaId=${anagraficaId}&id=${id}`));
  }

  analyzeAnagraficheFile(orgId: number, tracciatoId: number, file: File) {
    const form = new FormData();
    form.append('file', file);
    form.append('orgId', String(orgId));
    form.append('tracciatoId', String(tracciatoId));
    return this.http.post<AnagraficaImportResult>(
      `${environment.localApiUrl}registry/Anagrafiche/ImportaAnagraficheAnalyze`, form);
  }

  confirmAnagraficheImport(report: AnagraficaImportResult,
                           modalita: ModalitaSovrascrittura = ModalitaSovrascrittura.AggiornaTutto) {
    return this.http.post(
      `${environment.localApiUrl}registry/Anagrafiche/ImportaAnagraficheConfirm`, { report, modalita });
  }

  // ── Propagazione Anagrafica → Ordini Clienti (feature 120, issue #3368) ──
  // Il modale anagrafica parla SOLO con RegistryApi: non esiste una seconda base URL verso SaleApi.

  /**
   * Conteggi di impatto in RIGHE d'ordine. `sedeIds` DEVE includere anche gli identificativi delle
   * sedi ELIMINATE nella sessione: sono l'unico modo per popolare l'avviso "la sede X è stata
   * eliminata: N righe d'ordine mantengono l'indirizzo attuale" (FR-017).
   * Con SaleApi non raggiungibile la risposta è `disponibile: false`, mai un errore (FR-012).
   */
  async anteprimaImpattoOrdiniAsync(orgId: number, anagraficaId: number, sedeIds: number[] = []) {
    const query = sedeIds.map(id => `&sedeIds=${id}`).join('');
    return firstValueFrom(this.http.get<AnteprimaImpattoOrdini>(
      `${environment.localApiUrl}registry/Anagrafiche/AnteprimaImpattoOrdini`
      + `?orgId=${orgId}&anagraficaId=${anagraficaId}${query}`));
  }

  /**
   * Avvia la propagazione. Il corpo dichiara QUALI campi propagare, mai i valori: quelli vengono
   * riletti dal database di Registry (FR-030). La risposta porta il solo `correlationId`: il
   * conteggio che l'operatore ha letto è quello dell'anteprima, già in possesso del frontend.
   * Un `502` significa che la propagazione NON è partita (FR-014).
   */
  async propagaAiOrdiniAsync(request: PropagaAiOrdiniRequest) {
    return firstValueFrom(this.http.post<{ correlationId: string }>(
      `${environment.localApiUrl}registry/Anagrafiche/PropagaAiOrdini`, request));
  }
}

export interface ImpattoConteggio {
  righeInCorso: number;
  righeTotali: number;
  // Ordini distinti: dato DIAGNOSTICO, mai mostrato nel dialogo (FR-011).
  ordiniTotali: number;
}

export interface ImpattoSedeConteggio extends ImpattoConteggio {
  sedeId: number;
}

export interface AnteprimaImpattoOrdini {
  disponibile: boolean;
  anagrafica?: ImpattoConteggio | null;
  sedi: ImpattoSedeConteggio[];
}

export interface PropagaAiOrdiniRequest {
  orgId: number;
  anagraficaId: number;
  soloInCorso: boolean;
  campiAnagrafica: string[];
  campiUtente: string[];
  sedi: { sedeId: number; campi: string[] }[];
}
